// =====================================================
// 포인트 수동 증감 — 적립/사용 내역 하단 패널
// 이름/아이디로 회원을 검색해 선택한 뒤 적립·차감 처리
// =====================================================
import { useState } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'

interface MemberRow {
  id: number
  login_id: string
  name: string
  total_points: number
}

const SEARCH_LIMIT = 20

export default function AdminPointAdjustPanel({ onDone }: { onDone: () => void }) {
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<MemberRow[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)

  const [selected, setSelected] = useState<MemberRow | null>(null)
  const [type, setType] = useState<'1' | '2'>('1')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSearch = async () => {
    const kw = keyword.trim()
    if (!kw) return
    setSearching(true)
    setSelected(null)
    try {
      // 이름/아이디 어느 쪽으로 입력해도 찾히도록 두 조건을 각각 조회해 합친다.
      const [byName, byLoginId] = await Promise.all([
        fetchWithAuth(getApiUrl(`/marubit/api/member/admin/list?name=${encodeURIComponent(kw)}&limit=${SEARCH_LIMIT}`)),
        fetchWithAuth(getApiUrl(`/marubit/api/member/admin/list?login_id=${encodeURIComponent(kw)}&limit=${SEARCH_LIMIT}`)),
      ])
      if (!byName.ok || !byLoginId.ok) throw new Error('조회 실패')
      const [nameData, loginIdData] = await Promise.all([byName.json(), byLoginId.json()])

      const merged = new Map<number, MemberRow>()
      for (const m of [...(nameData.members ?? []), ...(loginIdData.members ?? [])]) {
        merged.set(m.id, m)
      }
      setResults([...merged.values()].slice(0, SEARCH_LIMIT))
    } catch {
      setResults([])
      alert('회원 조회에 실패했습니다.')
    } finally {
      setSearched(true)
      setSearching(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleSubmit = async () => {
    if (!selected) return
    const amountNum = Number(amount)
    if (!amount || amountNum < 1) { alert('포인트를 1 이상 입력해주세요.'); return }
    if (!description.trim()) { alert('내용을 입력해주세요.'); return }

    const action = type === '1' ? '적립' : '차감'
    if (!window.confirm(
      `${selected.name}(${selected.login_id})의 포인트를 ${amountNum.toLocaleString()}P ${action}하시겠습니까?`
    )) return

    setSaving(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/point/admin/adjust'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: selected.id,
          type: Number(type),
          amount: amountNum,
          description: description.trim(),
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail?.message ?? err.detail ?? '처리 실패')
      }
      const result = await res.json()

      const updated = { ...selected, total_points: result.balance_after }
      setSelected(updated)
      setResults(prev => prev.map(m => (m.id === updated.id ? updated : m)))
      setAmount('')
      setDescription('')
      alert(`${action} 완료되었습니다. 현재 포인트: ${result.balance_after.toLocaleString()}P`)
      onDone()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const expected = selected && amount && Number(amount) > 0
    ? type === '1'
      ? selected.total_points + Number(amount)
      : selected.total_points - Number(amount)
    : null

  return (
    <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 mt-6 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
      <h2 className="text-[15px] font-bold text-[#2a1f1a] mb-1">포인트 수동 증감</h2>
      <p className="text-[12px] text-gray-400 mb-4">
        회원을 검색해 선택한 뒤 포인트를 적립하거나 차감합니다. 처리 즉시 위 내역에 기록됩니다.
      </p>

      {/* 회원 검색 */}
      <div className="flex gap-2 items-end mb-3">
        <div className="flex flex-col gap-1 w-[240px]">
          <label className="text-[11px] text-[#5a3d32] font-medium">이름 / 아이디</label>
          <input
            type="text"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="회원 이름 또는 아이디"
            className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searching || !keyword.trim()}
          className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px] whitespace-nowrap"
        >
          {searching ? '조회 중...' : '검색'}
        </button>
      </div>

      {/* 검색 결과 */}
      {searched && (
        results.length === 0 ? (
          <p className="text-[12px] text-gray-400 py-3">검색 결과가 없습니다.</p>
        ) : (
          <div className="border border-[#f0e8e4] rounded-xl max-h-[200px] overflow-y-auto mb-4">
            {results.map(m => (
              <button
                key={m.id}
                onClick={() => setSelected(m)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left border-b border-[#f5f2f0] last:border-b-0 cursor-pointer transition-colors ${
                  selected?.id === m.id ? 'bg-[#fdf8f0]' : 'bg-white hover:bg-[#faf7f5]'
                }`}
              >
                <span className="text-[13px] text-[#2a1f1a]">
                  <span className="font-semibold">{m.name}</span>
                  <span className="text-[12px] text-gray-400 ml-2">({m.login_id})</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-[12px] text-[#887267] font-semibold whitespace-nowrap">
                    {m.total_points.toLocaleString()}P
                  </span>
                  {selected?.id === m.id && (
                    <span className="text-[11px] px-2 py-0.5 rounded font-medium bg-[#887267] text-white">선택됨</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )
      )}

      {/* 증감 입력 */}
      <div className={`border-t border-[#f5f2f0] pt-4 transition-opacity ${
        selected ? 'opacity-100' : 'opacity-40 pointer-events-none'
      }`}>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">구분</label>
            <div className="flex gap-2">
              {(['1', '2'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setType(v)}
                  className={`px-4 py-2 rounded-lg text-[13px] font-semibold border-2 transition-colors cursor-pointer h-[38px] ${
                    type === v
                      ? v === '1'
                        ? 'bg-[#e8f5e9] border-[#388e3c] text-[#388e3c]'
                        : 'bg-[#fce4ec] border-[#c62828] text-[#c62828]'
                      : 'bg-white border-[#e8e0dc] text-gray-400 hover:border-[#887267]'
                  }`}
                >
                  {v === '1' ? '적립' : '차감'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1 w-[140px]">
            <label className="text-[11px] text-[#5a3d32] font-medium">포인트</label>
            <input
              type="number"
              min={1}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="예: 500"
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors h-[38px]"
            />
          </div>

          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="text-[11px] text-[#5a3d32] font-medium">내용</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="예: 이벤트 참여 보상"
              maxLength={200}
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors h-[38px]"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={saving || !selected || !amount || !description.trim() || Number(amount) < 1}
            className="bg-[#887267] text-white text-[13px] font-semibold px-6 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px] whitespace-nowrap"
          >
            {saving ? '처리 중...' : '처리'}
          </button>
        </div>

        {selected && (
          <p className="text-[12px] text-gray-400 mt-3">
            대상: <span className="text-[#2a1f1a] font-semibold">{selected.name}</span>
            <span className="ml-1">({selected.login_id})</span>
            <span className="mx-2 text-gray-300">·</span>
            현재 <span className="text-[#887267] font-semibold">{selected.total_points.toLocaleString()}P</span>
            {expected !== null && (
              <>
                <span className="mx-2 text-gray-300">→</span>
                처리 후 <span className="text-[#887267] font-semibold">{expected.toLocaleString()}P</span>
                {type === '2' && expected < 0 && (
                  <span className="text-red-400 ml-2">⚠ 포인트 부족</span>
                )}
              </>
            )}
          </p>
        )}
      </div>
    </div>
  )
}
